import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { firebaseMessagingService } from "@/config/firebase";
import { TeacherNavigationProvider, useTeacherNavigation } from "@/contexts/TeacherNavigationContext";
import { TeacherThemeProvider, useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { TeacherHome } from "@/components/TeacherView/TeacherHome";
import { ActiveAlerts } from "./ActiveAlerts";
import { StudentsMonitored } from "./StudentsMonitored";
import { Notifications } from "./Notifications";
import { Game } from "./Game";
import { Profile } from "./Profile";
import { LiveActivity } from "./LiveActivity";
import { TeacherLiveScreens } from "./TeacherLiveScreens";
import { MyClasses } from "./MyClasses";
import { Reports } from "./Reports";
import { TeacherDocumentation } from "./Documentation";
import type { TeacherPage } from "@/contexts/TeacherNavigationContext";
import { dashboardService } from "@/services/dashboardService";
import {
  LayoutDashboard, AlertTriangle, Users, Bell, Award, Settings,
  LogOut, Menu, X, Radio, BookOpen, FileBarChart2, Search, Sun, Moon, BookOpenCheck, MonitorPlay,
} from "lucide-react";

// const PAGE_TITLES: Partial<Record<TeacherPage, string>> = {
//   home: 'Dashboard',
//   liveActivity: 'Live Activity',
//   liveScreens: 'Live Screens',
//   alerts: 'Alerts',
//   activeAlerts: 'Alerts',
//   myClasses: 'My Classes',
//   students: 'Students',
//   studentsMonitored: 'Students',
//   reports: 'Reports',
//   notifications: 'Notifications',
//   gamificationMVP: 'Gamification',
//   documentation: 'Documentation',
//   settings: 'Settings',
//   profile: 'Settings',
// };

// Colour key: 'danger' | 'blue' | 'emerald' | 'violet' | 'amber' | 'slate'
const navItems: { id: TeacherPage; label: string; icon: React.ElementType; badge?: 'alerts' }[] = [
  { id: 'home',          label: 'Dashboard',      icon: LayoutDashboard },
  { id: 'liveActivity',  label: 'Live Activity',  icon: Radio },
  { id: 'liveScreens',   label: 'Live Screens',   icon: MonitorPlay },
  { id: 'alerts',        label: 'Alerts',         icon: AlertTriangle, badge: 'alerts' },
  { id: 'myClasses',     label: 'My Classes',     icon: BookOpen },
  { id: 'students',      label: 'Students',       icon: Users },
  { id: 'reports',        label: 'Reports',        icon: FileBarChart2 },
  { id: 'notifications', label: 'Notifications',  icon: Bell },
  { id: 'gamificationMVP', label: 'Gamification', icon: Award },
  { id: 'documentation',   label: 'Documentation', icon: BookOpenCheck },
];

function TeacherContent() {
  const { user } = useApp();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useTeacherNavigation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { dm, toggleDarkMode } = useTeacherTheme();
  const [_schoolName, setSchoolName] = useState<string | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [studentResults, setStudentResults] = useState<{ id: string; name: string; email: string; riskScore: number }[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live clock
  const [now, setNow] = useState(new Date());

  // Fetch school name for this teacher
  useEffect(() => {
    dashboardService.getMySchool()
      .then(s => setSchoolName(s.name))
      .catch(() => setSchoolName(null));
  }, []);

  // Initialize FCM push notifications for this teacher
  useEffect(() => {
    if (!currentUser?.id) return;
    firebaseMessagingService.initializeForUser(currentUser.id).catch(() => {
      // Non-blocking — teacher still works without push notifications
    });
  }, [currentUser?.id]);

  // Navigate to alerts page when a push notification is clicked
  useEffect(() => {
    const handler = (e: Event) => {
      const page = (e as CustomEvent).detail?.page;
      if (page) setCurrentPage(page);
    };
    window.addEventListener('teacher-navigate', handler);
    return () => window.removeEventListener('teacher-navigate', handler);
  }, [setCurrentPage]);

  // Live clock — updates every second
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Unread notification count for bell badge
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await dashboardService.getDashboardActivity(1, 50);
        const count = res.data.filter(n => n.status === 'new' || n.status === 'escalated').length;
        setUnreadCount(count);
      } catch { /* silent */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60_000);
    return () => clearInterval(t);
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced student search
  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setStudentResults([]);
      setSearchOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await dashboardService.getMonitoredStudents(1, 100);
        const lower = q.toLowerCase();
        setStudentResults(
          res.data
            .filter(s => s.name.toLowerCase().includes(lower) || s.email?.toLowerCase().includes(lower))
            .slice(0, 6)
            .map(s => ({ id: s.id, name: s.name, email: s.email, riskScore: s.riskScore }))
        );
        setSearchOpen(true);
      } catch {
        setStudentResults([]);
      }
    }, 250);
  }, []);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home':           return <TeacherHome />;
      case 'liveActivity':   return <LiveActivity />;
      case 'liveScreens':    return <TeacherLiveScreens />;
      case 'alerts':
      case 'activeAlerts':   return <ActiveAlerts />;
      case 'myClasses':      return <MyClasses />;
      case 'students':
      case 'studentsMonitored': return <StudentsMonitored />;
      case 'reports':        return <Reports />;
      case 'notifications':  return <Notifications />;
      case 'gamificationMVP': return <Game />;
      case 'documentation':  return <TeacherDocumentation dm={dm} />;
      case 'settings':
      case 'profile':        return <Profile darkMode={dm} onToggleDarkMode={toggleDarkMode} />;
      default:               return <TeacherHome />;
    }
  };

  const handleLogout = async () => {
    // M16: Server-side logout to invalidate Redis-backed session tokens
    const token = sessionStorage.getItem('authToken');
    if (token) {
      try {
        await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* best-effort */ }
    }
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('user');
    localStorage.removeItem('authUid');
    localStorage.removeItem('userId');
    localStorage.removeItem('schoolId');
    navigate('/login');
  };

  const userInitials = (user?.name || 'T').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  // const pageTitle = PAGE_TITLES[currentPage] ?? 'Dashboard';

  return (
    <div className={`h-screen w-screen flex overflow-hidden ${dm ? 'bg-slate-950' : 'bg-slate-100'}`}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 h-full flex flex-col shrink-0
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${dm ? 'bg-slate-900 border-r border-slate-800' : 'bg-slate-900'}
      `}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-24">
              <img src="/shlogo3.png" alt="ShieldEd Haven" className="h-56 w-56 mt-[-5rem] mb-[-4rem] object-cover shrink-0" />
            </div>
            <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>
          </div>
          <p className="text-xs mt-2 text-slate-400 font-medium tracking-wide uppercase">Teacher Portal</p>
         
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto no-scrollbar">
          {navItems.map(({ id, label, icon: Icon }) => {
            const isActive = currentPage === id ||
              (id === 'alerts' && currentPage === 'activeAlerts') ||
              (id === 'students' && currentPage === 'studentsMonitored') ||
              (id === 'settings' && currentPage === 'profile');

            return (
              <button
                key={id}
                onClick={() => { setCurrentPage(id); setSidebarOpen(false); }}
                style={{
                  appearance: 'none', WebkitAppearance: 'none',
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                  fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
                  backgroundColor: isActive ? '#2563eb' : 'transparent',
                  color: isActive ? 'white' : '#cbd5e1',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { if (!isActive) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = '#1e293b'; b.style.color = 'white'; }}}
                onMouseLeave={e => { if (!isActive) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = 'transparent'; b.style.color = '#cbd5e1'; }}}
              >
                <Icon style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
                <span>{label}</span>
                {/* Live pulse dot for Live Activity */}
                {id === 'liveActivity' && (
                  <span className="ml-auto flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-slate-700">
          <div className="flex items-center gap-3 px-3 mb-3">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name || 'Teacher'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
            </div>
          </div>
          {/* Settings button moved here */}
          <button
            onClick={() => setCurrentPage('settings')}
            style={{
              appearance: 'none', WebkitAppearance: 'none',
              width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
              backgroundColor: currentPage === 'settings' || currentPage === 'profile' ? '#2563eb' : 'transparent',
              color: currentPage === 'settings' || currentPage === 'profile' ? 'white' : '#94a3b8',
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { 
              if (currentPage !== 'settings' && currentPage !== 'profile') {
                const b = e.currentTarget as HTMLButtonElement; 
                b.style.backgroundColor = '#1e293b'; 
                b.style.color = 'white';
              }
            }}
            onMouseLeave={e => { 
              if (currentPage !== 'settings' && currentPage !== 'profile') {
                const b = e.currentTarget as HTMLButtonElement; 
                b.style.backgroundColor = 'transparent'; 
                b.style.color = '#94a3b8';
              }
            }}
          >
            <Settings style={{ width: '1rem', height: '1rem' }} />
            <span>Settings</span>
          </button>
<button
            onClick={handleLogout}
            style={{
              appearance: 'none', WebkitAppearance: 'none',
              width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent', color: '#94a3b8',
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = '#1e293b'; b.style.color = '#f87171'; }}
            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = 'transparent'; b.style.color = '#94a3b8'; }}
          >
            <LogOut style={{ width: '1rem', height: '1rem' }} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">

        {/* Top Header */}
        <header className={`border-b flex items-center justify-between px-4 py-8 shrink-0 gap-3 ${dm ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>

          {/* Left: hamburger + search */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button className="lg:hidden shrink-0"
              onClick={() => setSidebarOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: dm ? '#94a3b8' : '#64748b' }}>
              <Menu style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>

            {/* Search bar */}
            <div ref={searchRef} className="relative w-full max-w-sm">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                onFocus={() => studentResults.length > 0 && setSearchOpen(true)}
                placeholder="Search students…"
                className={`w-full pl-8 pr-3 py-2 text-sm rounded-3xl border outline-none transition
                  ${dm
                    ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-600'
                    : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:bg-white'
                  }`}
              />
              {/* Dropdown */}
              {searchOpen && studentResults.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-1 rounded-xl shadow-xl border z-50 overflow-hidden
                  ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <p className={`px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                    Students
                  </p>
                  {studentResults.map(s => {
                    const initials = s.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                    const riskColor = s.riskScore >= 70 ? 'bg-red-100 text-red-600' : s.riskScore >= 40 ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600';
                    return (
                      <button
                        key={s.id}
                        onClick={() => { setCurrentPage('studentsMonitored'); setSearchQuery(''); setSearchOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${dm ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${riskColor}`}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{s.name}</p>
                          <p className="text-xs text-slate-400 truncate">{s.email}</p>
                        </div>
                        <span className={`ml-auto text-xs font-semibold shrink-0 ${s.riskScore >= 70 ? 'text-red-500' : s.riskScore >= 40 ? 'text-amber-500' : 'text-emerald-500'}`}>
                          {s.riskScore}%
                        </span>
                      </button>
                    );
                  })}
                  <div className={`px-3 py-2 border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`}>
                    <button
                      onClick={() => { setCurrentPage('studentsMonitored'); setSearchQuery(''); setSearchOpen(false); }}
                      className={`text-xs font-medium ${dm ? 'text-slate-400 hover:text-slate-200' : 'text-blue-600 hover:text-blue-700'}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      View all students →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: date/time + controls */}
          <div className="flex items-center gap-3 shrink-0">

            {/* Date & time */}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className={`text-sm font-semibold tabular-nums ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>

            <div className={`hidden sm:block w-px h-6 ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              title={dm ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition ${dm ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-700 hover:bg-slate-100'}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none' }}
            >
              {dm
                ? <span style={{ color: '#e2e8f0' }}><Sun size={24} /></span>
                : <span style={{ color: '#000000' }}><Moon size={24} /></span>
              }
            </button>

            {/* Bell */}
            <button
              onClick={() => setCurrentPage('notifications')}
              title="Notifications"
              className={`relative w-8 h-8 flex items-center justify-center rounded-lg transition ${dm ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-700 hover:bg-slate-100'}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none' }}
            >
              <span style={{ color: dm ? '#e2e8f0' : '#000000' }}><Bell size={24} /></span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 right-1 flex items-center justify-center h-4 min-w-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* Avatar */}
            <button
              onClick={() => setCurrentPage('profile')}
              title="Profile"
              className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0 transition hover:bg-blue-700 hover:scale-105"
              style={{ border: 'none', cursor: 'pointer', outline: 'none', padding: 0, borderRadius: '50%' }}
            >
              {userInitials}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto no-scrollbar ${currentPage === 'documentation' ? '' : 'p-6'} ${dm ? 'bg-slate-950 text-slate-100' : ''}`}>
          {renderCurrentPage()}
        </main>
      </div>
      
    </div>
  );
}

export function TeacherDashboard() {
  return (
    <TeacherThemeProvider>
      <TeacherNavigationProvider>
        <TeacherContent />
      </TeacherNavigationProvider>
    </TeacherThemeProvider>
  );
}

export default TeacherDashboard;
